<script lang="ts">
  import { ApiError } from "@shared/api/client";
  import { competitorServicesApi } from "@shared/api/competitor-services";
  import type {
    CompetitorServiceSalesMaterial,
    CompetitorServiceSalesMaterialCreateRequest,
    CompetitorServiceSalesMaterialUpdateRequest,
  } from "@shared/types/competitor-service";

  interface Props {
    competitorServiceId: string;
    canEdit?: boolean;
  }

  interface MaterialDraft {
    title: string;
    url: string;
  }

  interface MaterialTableRow extends MaterialDraft {
    key: string;
    materialId: string | null;
    source: CompetitorServiceSalesMaterial | null;
  }

  const { competitorServiceId, canEdit = false }: Props = $props();

  let materials = $state<CompetitorServiceSalesMaterial[]>([]);
  let rows = $state<MaterialTableRow[]>([]);
  let loading = $state(false);
  let busyKeys = $state<Set<string>>(new Set());
  let errorMessage = $state<string | null>(null);
  let blankSequence = 0;

  $effect(() => {
    if (!competitorServiceId) return;
    void loadMaterials();
  });

  $effect(() => {
    rows = rowsFromMaterials(materials);
  });

  async function loadMaterials(): Promise<void> {
    loading = true;
    errorMessage = null;
    try {
      const res =
        await competitorServicesApi.listSalesMaterials(competitorServiceId);
      materials = res.items;
    } catch (e) {
      errorMessage = formatError(e, "営業資料を読み込めませんでした");
      materials = [];
    } finally {
      loading = false;
    }
  }

  function rowsFromMaterials(
    items: CompetitorServiceSalesMaterial[],
  ): MaterialTableRow[] {
    const materialRows = items.map((material) => ({
      key: material.id,
      materialId: material.id,
      source: material,
      title: material.title,
      url: material.url,
    }));
    return canEdit ? [...materialRows, createBlankRow()] : materialRows;
  }

  function createBlankRow(): MaterialTableRow {
    blankSequence += 1;
    return {
      key: `external-material-blank-${blankSequence}`,
      materialId: null,
      source: null,
      title: "",
      url: "",
    };
  }

  function isBlankDraft(draft: MaterialDraft): boolean {
    return draft.title.trim().length === 0 && draft.url.trim().length === 0;
  }

  function isCompleteDraft(draft: MaterialDraft): boolean {
    return draft.title.trim().length > 0 && draft.url.trim().length > 0;
  }

  function validateDraft(draft: MaterialDraft): string | null {
    if (isBlankDraft(draft)) return null;
    if (!isCompleteDraft(draft)) {
      return "タイトルとURLの両方が入力された行のみ保存されます";
    }
    return null;
  }

  function normalizeRows(nextRows: MaterialTableRow[]): MaterialTableRow[] {
    if (!canEdit) return nextRows;
    const contentRows = nextRows.filter(
      (row) => row.materialId !== null || !isBlankDraft(row),
    );
    return [...contentRows, createBlankRow()];
  }

  function setBusy(key: string, busy: boolean): void {
    const next = new Set(busyKeys);
    if (busy) next.add(key);
    else next.delete(key);
    busyKeys = next;
  }

  function updateRowField(
    key: string,
    field: keyof MaterialDraft,
    value: string,
  ): void {
    if (!canEdit) return;
    errorMessage = null;
    rows = normalizeRows(
      rows.map((row) => (row.key === key ? { ...row, [field]: value } : row)),
    );
  }

  function currentMaterialFor(
    row: MaterialTableRow,
  ): CompetitorServiceSalesMaterial | null {
    if (!row.materialId) return null;
    return (
      materials.find((material) => material.id === row.materialId) ?? row.source
    );
  }

  function nextSortOrder(): number {
    return (
      materials.reduce(
        (max, material) => Math.max(max, material.sort_order),
        -1,
      ) + 1
    );
  }

  function buildCreatePayload(
    draft: MaterialDraft,
  ): CompetitorServiceSalesMaterialCreateRequest {
    return {
      title: draft.title.trim(),
      url: draft.url.trim(),
      sort_order: nextSortOrder(),
    };
  }

  function buildPatchPayload(
    current: CompetitorServiceSalesMaterial,
    draft: MaterialDraft,
  ): CompetitorServiceSalesMaterialUpdateRequest {
    const patch: CompetitorServiceSalesMaterialUpdateRequest = {};
    const title = draft.title.trim();
    const url = draft.url.trim();
    if (title !== current.title) patch.title = title;
    if (url !== current.url) patch.url = url;
    return patch;
  }

  function trimRowInPlace(key: string, draft: MaterialDraft): void {
    rows = rows.map((row) =>
      row.key === key
        ? { ...row, title: draft.title.trim(), url: draft.url.trim() }
        : row,
    );
  }

  async function commitRow(key: string): Promise<void> {
    if (!canEdit || busyKeys.has(key)) return;
    const row = rows.find((candidate) => candidate.key === key);
    if (!row) return;

    const validationMessage = validateDraft(row);
    if (validationMessage) {
      errorMessage = validationMessage;
      return;
    }
    if (!isCompleteDraft(row)) {
      errorMessage = null;
      return;
    }

    const draft: MaterialDraft = {
      title: row.title.trim(),
      url: row.url.trim(),
    };
    trimRowInPlace(key, draft);

    setBusy(key, true);
    errorMessage = null;
    try {
      if (row.materialId) {
        const current = currentMaterialFor(row);
        if (!current) return;
        const patch = buildPatchPayload(current, draft);
        if (Object.keys(patch).length === 0) return;
        const updated = await competitorServicesApi.updateSalesMaterial(
          competitorServiceId,
          row.materialId,
          patch,
        );
        materials = materials.map((material) =>
          material.id === updated.id ? updated : material,
        );
      } else {
        const created = await competitorServicesApi.createSalesMaterial(
          competitorServiceId,
          buildCreatePayload(draft),
        );
        materials = [...materials, created];
      }
    } catch (e) {
      errorMessage = formatError(e, "保存に失敗しました");
    } finally {
      setBusy(key, false);
    }
  }

  async function deleteRow(row: MaterialTableRow): Promise<void> {
    if (!canEdit || !row.materialId || busyKeys.has(row.key)) return;
    if (!confirm(`「${row.title.trim()}」を削除しますか?`)) return;
    setBusy(row.key, true);
    errorMessage = null;
    try {
      await competitorServicesApi.deleteSalesMaterial(
        competitorServiceId,
        row.materialId,
      );
      materials = materials.filter(
        (material) => material.id !== row.materialId,
      );
    } catch (e) {
      errorMessage = formatError(e, "削除に失敗しました");
    } finally {
      setBusy(row.key, false);
    }
  }

  function handleEnter(event: KeyboardEvent, key: string): void {
    if (event.key !== "Enter") return;
    event.preventDefault();
    void commitRow(key);
  }

  function formatError(e: unknown, fallback: string): string {
    if (e instanceof ApiError) return e.message;
    if (e instanceof Error) return e.message;
    return fallback;
  }
</script>

<section
  class="sales-materials-editor"
  data-testid="external-solution-sales-materials-editor"
  aria-busy={loading || busyKeys.size > 0}
>
  {#if loading}
    <p class="muted small">読み込み中…</p>
  {/if}

  {#if errorMessage}
    <p
      class="error"
      role="alert"
      data-testid="external-solution-sales-materials-error"
    >
      {errorMessage}
    </p>
  {/if}

  <div
    class="materials-table-wrap"
    data-testid="external-solution-sales-materials-list"
  >
    <table class="materials-table">
      <colgroup>
        <col class="title-col" />
        <col class="url-col" />
        {#if canEdit}
          <col class="actions-col" />
        {/if}
      </colgroup>
      <thead>
        <tr>
          <th>タイトル</th>
          <th>URL</th>
          {#if canEdit}
            <th aria-label="操作"></th>
          {/if}
        </tr>
      </thead>
      <tbody>
        {#if rows.length === 0 && !loading}
          <tr>
            <td
              class="empty-cell"
              colspan={canEdit ? 3 : 2}
              data-testid="external-solution-sales-materials-empty"
            >
              営業資料はまだありません。
            </td>
          </tr>
        {:else}
          {#each rows as row (row.key)}
            {@const rowBusy = busyKeys.has(row.key)}
            <tr
              class:input-row={row.materialId === null}
              class:busy={rowBusy}
              data-testid="external-solution-sales-materials-row"
              data-material-id={row.materialId ?? ""}
            >
              <td>
                {#if canEdit}
                  <input
                    type="text"
                    aria-label="営業資料タイトル"
                    data-testid="external-solution-sales-materials-title-input"
                    value={row.title}
                    placeholder={row.materialId === null
                      ? "資料タイトル"
                      : undefined}
                    disabled={rowBusy}
                    oninput={(event) =>
                      updateRowField(
                        row.key,
                        "title",
                        (event.currentTarget as HTMLInputElement).value,
                      )}
                    onblur={() => void commitRow(row.key)}
                    onkeydown={(event) => handleEnter(event, row.key)}
                  />
                {:else}
                  <a
                    class="cell-link title-link"
                    href={row.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={row.url}
                    data-testid="external-solution-sales-materials-title-link"
                  >
                    {row.title}
                  </a>
                {/if}
              </td>
              <td>
                {#if canEdit}
                  <input
                    type="url"
                    aria-label="営業資料URL"
                    data-testid="external-solution-sales-materials-url-input"
                    value={row.url}
                    placeholder={row.materialId === null
                      ? "https://..."
                      : undefined}
                    disabled={rowBusy}
                    oninput={(event) =>
                      updateRowField(
                        row.key,
                        "url",
                        (event.currentTarget as HTMLInputElement).value,
                      )}
                    onblur={() => void commitRow(row.key)}
                    onkeydown={(event) => handleEnter(event, row.key)}
                  />
                {:else}
                  <a
                    class="cell-link url-link"
                    href={row.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={row.url}
                    data-testid="external-solution-sales-materials-url-link"
                  >
                    {row.url}
                  </a>
                {/if}
              </td>
              {#if canEdit}
                <td class="actions-cell">
                  {#if row.materialId}
                    <button
                      type="button"
                      class="delete-button"
                      aria-label="営業資料を削除"
                      title="削除"
                      data-testid="external-solution-sales-materials-delete-button"
                      disabled={rowBusy}
                      onmousedown={(event) => event.preventDefault()}
                      onclick={() => void deleteRow(row)}
                    >
                      削除
                    </button>
                  {/if}
                </td>
              {/if}
            </tr>
          {/each}
        {/if}
      </tbody>
    </table>
  </div>
</section>

<style>
  .sales-materials-editor {
    display: grid;
    gap: 10px;
    min-width: 0;
  }

  .materials-table-wrap {
    width: 100%;
    overflow-x: auto;
    border: 1px solid #dbe3ef;
    border-radius: 8px;
    background: #fff;
  }

  .materials-table {
    width: 100%;
    min-width: 460px;
    border-collapse: collapse;
    table-layout: fixed;
  }

  .title-col {
    width: 42%;
  }

  .url-col {
    width: 58%;
  }

  .actions-col {
    width: 72px;
  }

  th,
  td {
    border-bottom: 1px solid #e5edf6;
    border-right: 1px solid #e5edf6;
    padding: 0;
    text-align: left;
    vertical-align: middle;
  }

  th:last-child,
  td:last-child {
    border-right: 0;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }

  th {
    background: #f8fafc;
    color: #475569;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.4;
    padding: 8px 10px;
  }

  tr.busy {
    opacity: 0.7;
  }

  input {
    width: 100%;
    min-width: 0;
    min-height: 36px;
    border: 0;
    border-radius: 0;
    background: transparent;
    color: #0f172a;
    font: inherit;
    font-size: 13px;
    line-height: 1.4;
    padding: 8px 10px;
  }

  input::placeholder {
    color: #94a3b8;
  }

  input:focus {
    position: relative;
    z-index: 1;
    outline: 2px solid #2563eb;
    outline-offset: -2px;
    background: #fff;
  }

  input:disabled {
    color: #334155;
    opacity: 1;
    -webkit-text-fill-color: #334155;
  }

  .actions-cell {
    padding: 0 8px;
    text-align: center;
  }

  .delete-button {
    min-height: 28px;
    border: 1px solid #fecaca;
    border-radius: 6px;
    background: #fff;
    color: #b91c1c;
    cursor: pointer;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.2;
    padding: 4px 8px;
  }

  .delete-button:hover:not(:disabled) {
    background: #fef2f2;
  }

  .delete-button:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  .cell-link {
    display: block;
    min-width: 0;
    overflow: hidden;
    color: #1d4ed8;
    font-size: 13px;
    line-height: 1.4;
    padding: 8px 10px;
    text-decoration: none;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .cell-link:hover {
    text-decoration: underline;
  }

  .title-link {
    font-weight: 700;
  }

  .empty-cell {
    color: #64748b;
    font-size: 13px;
    padding: 14px 10px;
  }

  .muted {
    margin: 0;
    color: #6b7280;
  }

  .small {
    font-size: 12px;
  }

  .error {
    margin: 0;
    color: #991b1b;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 6px;
    padding: 8px 10px;
    font-size: 12px;
    font-weight: 700;
  }

  @media (max-width: 640px) {
    .materials-table {
      min-width: 420px;
    }

    input {
      min-height: 40px;
      font-size: 13px;
      padding: 9px 8px;
    }
  }
</style>
